import { useState } from 'react';

export default function Footer() {
  const [hoveredService, setHoveredService] = useState<number | null>(null);

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    
    const targetId = href.replace('#', '');
    const targetElement = document.getElementById(targetId);
    
    if (targetElement) {
      const headerOffset = 80;
      const elementPosition = targetElement.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  const services = [
    { name: 'デジポップブースト', desc: 'DX推進伴走支援', color: '#153577', icon: 'ri-rocket-line', href: 'https://dx.digipop.co.jp/' },
    { name: 'ウェブポップブースト', desc: 'デジタルマーケティング支援', color: '#9a1e23', icon: 'ri-global-line', href: 'https://webpop.in/' },
    { name: 'アイポップブースト', desc: '生成AI活用支援', color: '#037555', icon: 'ri-brain-line', href: 'https://digipop.co.jp/product/aipop/' },
    { name: 'ソーシャルポップブースト', desc: 'SNS運用支援', color: '#ea5614', icon: 'ri-instagram-line', href: 'https://digipop.co.jp/product/socialpop/' },
  ];

  const navLinks = [
    { label: 'デジポップブーストとは', href: '#about' },
    { label: '特徴', href: '#features' },
    { label: '支援内容', href: '#service' },
    { label: '支援の流れ', href: '#flow' },
    { label: '料金', href: '#price' },
    { label: 'よくある質問', href: '#faq' },
  ];

  const contactLinks = [
    { label: '無料DX推進診断', href: 'https://digipop.co.jp/webservice/dx-diagnosis/', icon: 'ri-file-search-line', external: true },
    { label: '無料相談する', href: 'https://imachika.com/viewform/?form_id=698b466b23679c3f7a84ae56', icon: 'ri-mail-send-line', external: true },
    { label: 'LINEでお問い合わせ', href: 'https://lin.ee/Xm9zGrPi', icon: 'ri-line-fill', external: true },
    { label: 'ChatWorkでお問い合わせ', href: 'https://www.chatwork.com/candypop0124', icon: 'ri-chat-3-line', external: true },
  ];

  return (
    <footer className="relative overflow-hidden bg-[#f5f3f0]">
      {/* 背景装飾 */}
      <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-[#9a1e23]/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3"></div>
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#037555]/5 rounded-full blur-3xl translate-y-1/2 -translate-x-1/3"></div>

      {/* メインフッター */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 pt-16 pb-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8 mb-12">

          {/* ロゴ・説明エリア */}
          <div className="lg:col-span-4">
            <div className="flex items-center gap-3 mb-5">
              <img
                src="https://static.readdy.ai/image/a52a13a88e2ccb39325c6dcb34729469/b788f00815a4117892cd80fd8891e486.png"
                alt="デジポップブースト"
                className="h-10 w-auto"
              />
            </div>
            <p className="text-sm text-gray-600 leading-relaxed mb-5 max-w-xs">
              高知県香美市を拠点に、地方からデジタルで世界に挑戦。DX未経験でも安心の伴走型支援で、御社のデジタル変革を実現します。
            </p>
            <div className="flex items-center gap-2 text-gray-500 text-sm">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-map-pin-2-line text-sm text-[#9a1e23]"></i>
              </div>
              <span>高知県香美市土佐山田町西本町1丁目5番26号</span>
            </div>
          </div>

          {/* ナビリンク */}
          <div className="lg:col-span-2">
            <h4 className="text-sm font-bold text-[#12335f] mb-4 flex items-center gap-2">
              <span className="w-4 h-0.5 bg-[#9a1e23] rounded-full inline-block"></span>
              <a href="#about" onClick={(e) => handleNavClick(e, '#about')}>サイトマップ</a>
            </h4>
            <nav className="space-y-2.5">
              {navLinks.map((link, idx) => (
                <a
                  key={idx}
                  href={link.href}
                  onClick={(e) => handleNavClick(e, link.href)}
                  className="block text-sm text-gray-600 hover:text-[#9a1e23] hover:translate-x-1 transition-all duration-200 cursor-pointer"
                >
                  {link.label}
                </a>
              ))}
            </nav>
          </div>

          {/* お問い合わせリンク */}
          <div className="lg:col-span-3">
            <h4 className="text-sm font-bold text-[#12335f] mb-4 flex items-center gap-2">
              <span className="w-4 h-0.5 bg-[#037555] rounded-full inline-block"></span>
              <a href="#contact">お問い合わせ</a>
            </h4>
            <nav className="space-y-2.5">
              {contactLinks.map((link, idx) => (
                <a
                  key={idx}
                  href={link.href}
                  {...(link.external ? { target: '_blank', rel: 'noopener noreferrer nofollow' } : {})}
                  className="flex items-center gap-2.5 text-sm text-gray-600 hover:text-[#037555] transition-all duration-200 cursor-pointer group"
                >
                  <div className="w-5 h-5 flex items-center justify-center text-gray-400 group-hover:text-[#037555] transition-colors">
                    <i className={`${link.icon} text-sm`}></i>
                  </div>
                  {link.label}
                </a>
              ))}
            </nav>
          </div>

          {/* サービス一覧 */}
          <div className="lg:col-span-3">
            <h4 className="text-sm font-bold text-[#12335f] mb-4 flex items-center gap-2">
              <span className="w-4 h-0.5 bg-[#ea5614] rounded-full inline-block"></span>
              <a href="#service">サービス</a>
            </h4>
            <div className="space-y-2">
              {services.map((service, idx) => (
                service.href ? (
                  <a
                    key={idx}
                    href={service.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex items-center gap-3 p-2 rounded-lg transition-all duration-200 cursor-pointer hover:bg-white/60"
                    onMouseEnter={() => setHoveredService(idx)}
                    onMouseLeave={() => setHoveredService(null)}
                  >
                    <div
                      className="w-8 h-8 flex items-center justify-center rounded-lg transition-all duration-200 flex-shrink-0"
                      style={{
                        backgroundColor: hoveredService === idx ? service.color : `${service.color}15`,
                      }}
                    >
                      <i
                        className={`${service.icon} text-sm transition-colors duration-200`}
                        style={{ color: hoveredService === idx ? '#fff' : service.color }}
                      ></i>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-700 group-hover:text-gray-900 transition-colors leading-tight">
                        {service.name}
                      </p>
                      <p className="text-[11px] text-gray-400 group-hover:text-gray-500 transition-colors leading-tight mt-0.5">
                        {service.desc}
                      </p>
                    </div>
                  </a>
                ) : (
                  <div
                    key={idx}
                    className="group flex items-center gap-3 p-2 rounded-lg transition-all duration-200 cursor-pointer hover:bg-white/60"
                    onMouseEnter={() => setHoveredService(idx)}
                    onMouseLeave={() => setHoveredService(null)}
                  >
                    <div
                      className="w-8 h-8 flex items-center justify-center rounded-lg transition-all duration-200 flex-shrink-0"
                      style={{
                        backgroundColor: hoveredService === idx ? service.color : `${service.color}15`,
                      }}
                    >
                      <i
                        className={`${service.icon} text-sm transition-colors duration-200`}
                        style={{ color: hoveredService === idx ? '#fff' : service.color }}
                      ></i>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-700 group-hover:text-gray-900 transition-colors leading-tight">
                        {service.name}
                      </p>
                      <p className="text-[11px] text-gray-400 group-hover:text-gray-500 transition-colors leading-tight mt-0.5">
                        {service.desc}
                      </p>
                    </div>
                  </div>
                )
              ))}
            </div>
          </div>
        </div>

        {/* 区切り線 */}
        <div className="border-t border-gray-200 pt-6">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-6">
              <p className="text-xs text-gray-500">
                &copy; 合同会社デジポップ All Rights Reserved.
              </p>
              <a
                href="https://digipop.co.jp/"
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="text-xs text-gray-500 hover:text-[#9a1e23] transition-colors cursor-pointer flex items-center gap-1"
              >
                <div className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className="ri-external-link-line text-[11px]"></i>
                </div>
                公式サイトへ
              </a>
            </div>
            <a
              href="https://readdy.ai/?ref=logo"
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
            >
              Powered by Readdy
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
